import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { Spacing } from "../../Redesign/Spacing";
import type { DigitProps, DigitRowInputProps, DigitRowProps } from "./digit.interface";
import { DigitRowProvider, useDigitRowContext } from "./DigitContext";
import "./digit.scss";
import { Paragraph } from "../../Elements/Paragraph";

function Digit({ upperCase, index, count, autoFocus, type = "password", onBlur, fieldId }: DigitProps) {
	const { digits, setDigit, setValue } = useDigitRowContext();
	const [metaPressed, setMetaPressed] = useState(false);
	const value = digits[index];

	const isAutoFocused = index === 0 ? autoFocus : false;
	const inputRef = useRef<HTMLInputElement>(null);

	function focusInput(sibling: "next" | "previous") {

		if (sibling === "next") {
			const nextElement = (inputRef.current?.["nextElementSibling"] as HTMLInputElement | null);
			if (nextElement?.selectionStart && nextElement.selectionStart >= 0) {
				nextElement.selectionStart = 0;
			}
			nextElement?.focus();
		} else {
			(inputRef.current?.["previousElementSibling"] as HTMLElement | null)?.focus();
		}
	}

	function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
		const value = event.target?.value;

		setDigit(index, value[0], upperCase);

		console.log(`Digit value:`, digits);

		// We have to set cursor to the next input. If user do not release the key there is no up phase.
		if (!metaPressed && value && value.length >= 1) {
			focusInput("next");
		}
	}

	function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
		const input = event.currentTarget;
		// key up + meta events is not always working so we save the information from key down.
		const isMetaKey = (event.ctrlKey || event.metaKey);
		setMetaPressed(isMetaKey);

		if (!isMetaKey && event.key === "Backspace" && input.selectionStart == 0) {
			focusInput("previous");
			return;
		}

		// If user types a new letter it erases an existing one.
		if (!isMetaKey && event.key !== "Backspace" && input.selectionStart == 0 && value?.length === 1) {
			setDigit(index, "", upperCase);
			return
		}
	}

	function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {

		event.preventDefault();

		const value = event.clipboardData.getData("text");

		setValue(value.slice(0, count), upperCase);
	}

	useEffect(() => {
		if (isAutoFocused && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isAutoFocused]);

	const inputId = fieldId ? (fieldId + "_" + index) : undefined;

	return (<input
		aria-label={inputId} id={inputId}
		pattern={type === "number" || type === "otp" ? "[0-9]" : undefined}
		type={type === "password" || type === "otp" ? "password" : "text"}
		maxLength={1}
		inputMode={type === "number" || type === "otp" ? "numeric" : "text"}
		onChange={handleChange}
		onKeyDown={handleKeyDown}
		onPaste={handlePaste}
		onBlur={onBlur}
		ref={inputRef}
		value={value}
		className={clsx("digit", { filled: value !== "" })}
	/>);
}

function DigitRow({
										digits,
										count = 3,
										onChange,
										onBlur,
										type = "password",
										textLeftOfDigits,
										fieldId,
										upperCase
									}: DigitRowProps) {

	const fields = Array.from({ length: count }, (_, index) => (
		<Digit upperCase={upperCase} count={count} fieldId={fieldId} key={index} value={digits && digits[index]} index={index} autoFocus={index === 0} type={type} onBlur={onBlur} />
	));

	return (
		<DigitRowProvider onChange={onChange}>
			<Spacing justifyContent="center" alignItems="center" fullWidthChildren={false} flexWrap={"nowrap"}>
				{textLeftOfDigits && <span>{textLeftOfDigits}</span>}
				{fields}
			</Spacing>
		</DigitRowProvider>
	);
}

function DigitRowInput({
												 digits,
												 count,
												 onChange,
												 type,
												 error,
												 onBlur,
												 fieldId,
												 label,
													upperCase
											 }: DigitRowInputProps) {

	return (<div className={"digitInputWrapper"}>
		{label && <div className={"digitInputLabel"}>
			<Paragraph text={label} />
		</div>}
		<div className={"digitInput digit" + count} aria-label={fieldId} id={fieldId}>
			<DigitRow upperCase={upperCase} fieldId={fieldId} digits={digits} type={type} textLeftOfDigits={undefined} count={count} onBlur={onBlur} onChange={onChange} />
			{error && <div className="textInput-notifications">
				<div id={fieldId ? fieldId + "-error" : undefined} className="textInput_message">
					<div className="textInput_corner error"></div>
					<p className=" textInput_text error">{error}</p></div>
			</div>}
		</div>
	</div>);
}

export { DigitRow, Digit, DigitRowInput };
